import React, { useState, useMemo, useRef, useEffect } from "react";
import { List } from "react-window";

const ITEM_HEIGHT = 40;

const highlightText = (text, query) => {
  if (!query) return <span>{text}</span>;

  const parts = text.split(new RegExp(`(${query})`, "gi"));

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="bg-yellow-200 font-semibold">
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
};

const CustomAutocomplete = ({
  options = [],
  getOptionLabel = (opt) => opt.label ?? "",
  getOptionKey = (opt) => opt.id ?? getOptionLabel(opt),
  isOptionEqual = (a, b) => getOptionKey(a) === getOptionKey(b),
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const listRef = useRef();

  // Filter options
  const filteredOptions = useMemo(() => {
    return options.filter((item) => {
      const label = getOptionLabel(item).toLowerCase();
      const match = label.includes(query.toLowerCase());
      const alreadySelected = selected.some((s) =>
        isOptionEqual(s, item)
      );
      return match && !alreadySelected;
    });
  }, [query, options, selected]);

  // Scroll active item
  useEffect(() => {
    listRef.current?.scrollToItem(activeIndex);
  }, [activeIndex]);

  // Keyboard handling
  const handleKeyDown = (e) => {
    if (!open) return;

    switch (e.key) {
      case "ArrowDown":
        setActiveIndex((prev) =>
          Math.min(prev + 1, filteredOptions.length - 1)
        );
        break;

      case "ArrowUp":
        setActiveIndex((prev) => Math.max(prev - 1, 0));
        break;

      case "Enter":
        if (filteredOptions[activeIndex]) {
          selectItem(filteredOptions[activeIndex]);
        }
        break;

      case "Escape":
        setOpen(false);
        break;

      case "Backspace":
        if (!query && selected.length) {
          setSelected((prev) => prev.slice(0, -1));
        }
        break;
    }
  };

  const selectItem = (item) => {
    setSelected((prev) => [...prev, item]);
    setQuery("");
    setActiveIndex(0);
  };

  const removeItem = (item) => {
    setSelected((prev) =>
      prev.filter((i) => !isOptionEqual(i, item))
    );
  };

  const Row = ({ index, style }) => {
    const item = filteredOptions[index];
  
    // 🚨 SAFETY CHECK
    if (typeof item !== "object" || item === null) {
      return <div style={style}>Invalid item</div>;
    }
  
    const label = getOptionLabel(item);
  
    // 🚨 EXTRA GUARD (important)
    if (typeof label !== "string") {
      console.error("getOptionLabel must return string", label);
      return <div style={style}>Invalid label</div>;
    }
  
    const isActive = index === activeIndex;
  
    return (
      <div
        style={style}
        className={`px-3 ${
          isActive ? "bg-blue-100" : "hover:bg-gray-100"
        }`}
      >
        {highlightText(label, query)}
      </div>
    );
  };

  return (
    <div className="w-96 relative">
      {/* Input + Chips */}
      <div
        className="border rounded px-2 py-1 flex flex-wrap gap-1 items-center"
        onClick={() => setOpen(true)}
      >
        {selected.map((item) => (
          <div
            key={getOptionKey(item)}
            className="bg-blue-100 px-2 py-1 rounded flex items-center gap-1"
          >
            {getOptionLabel(item)}
            <span
              className="cursor-pointer"
              onClick={() => removeItem(item)}
            >
              ×
            </span>
          </div>
        ))}

        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className="flex-1 outline-none px-1 py-1"
          placeholder="Search..."
        />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute w-full bg-white border mt-1 shadow-lg z-10">
          {filteredOptions.length === 0 ? (
            <div className="p-2 text-gray-500">No results</div>
          ) : (
            <List
              ref={listRef}
              height={200}
              itemCount={filteredOptions.length}
              itemSize={ITEM_HEIGHT}
              width="100%"
            >
              {Row}
            </List>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomAutocomplete;